import { useQuery } from "@tanstack/react-query";
import {
  ControlCode,
  Detection,
  formatFileName,
  queries,
  ThreatHunt,
  ThreatHuntQueries,
  useAuthConfig,
  useCompile,
} from "@theprelude/core";
import {
  Button,
  CodeIcon,
  InfoIcon,
  PlusIcon,
  RestartIcon,
  SignalIcon,
  TargetIcon,
  ToggleGroup,
  ToggleItem,
} from "@theprelude/ds";
import { useEffect, useState } from "react";
import type { Merge, SetOptional } from "type-fest";
import { v4 } from "uuid";
import YAML from "yaml";
import { CompileResults } from "../compile-results/compile-results";
import { DefaultTooltip } from "../default-tooltip";
import Editor from "../editor/editor";
import { Flag } from "../flag/flag";
import { EditDetections, EditThreatHunts } from "./editor-components";

type EditorTabs = "sourceCode" | "detections" | "threatHunts" | "readme";
type EditorStatus = "created" | "modified" | "deleted" | "unchanged";

export type EditableThreatHunt = Merge<
  SetOptional<ThreatHunt, "test_id" | "created">,
  { status: EditorStatus }
>;

export type EditableDetection = Merge<
  Partial<Detection>,
  {
    id: string;
    rule: string;
    status: EditorStatus;
  }
>;

export type TestEditorState = {
  sourceCode: string;
  detections: EditableDetection[];
  threatHunts: EditableThreatHunt[];
  readme: string;
  sourceTestId: string;
};

export function formatEditableDetectionsFromAI(
  detections: string[],
): EditableDetection[] {
  return detections.map((d) => {
    return { rule: d, status: "created", id: v4() };
  });
}

export function formatEditableThreatHuntsFromAI(
  threatHunts: ThreatHuntQueries[],
): EditableThreatHunt[] {
  return threatHunts.map((t) => {
    return { ...t, status: "created", id: v4() };
  });
}

export function formatEditableDetections(
  detections: Detection[],
): EditableDetection[] {
  return detections.map((d) => {
    return { ...d, rule: YAML.stringify(d.rule), status: "unchanged" };
  });
}

export function formatEditableThreatHunts(
  threatHunts: ThreatHunt[],
): EditableThreatHunt[] {
  return threatHunts.map((t) => {
    return { ...t, status: "unchanged" };
  });
}

const ruleTemplate = `title:
detection:
# Options for 'condition': ['GrandparentImage', 'GrandparentCommandLine', 'ParentImage', 'ParentCommandLine', 'Image', 'CommandLine', 'RemoteIp', 'RemotePort']
condition:
logsource:
  product:
  category:
# Optional:
author:
description:`;

export const TestEditorField: React.FC<{
  state: TestEditorState;
  onChange: (newState: TestEditorState) => void;
  isCompiled: boolean;
  setIsCompiled: (b: boolean) => void;
}> = ({ state, onChange, isCompiled, setIsCompiled }) => {
  const authConfig = useAuthConfig();
  const compile = useCompile();
  const [activeTab, setActiveTab] = useState<EditorTabs>("sourceCode");
  const [currentState] = useState(state);
  const [compiledSourceCode, setCompiledSourceCode] = useState<string>(
    currentState.sourceCode,
  );

  const updateSourceCode = (value: string) => {
    if (value === currentState.sourceCode) {
      onChange({ ...state, sourceCode: value });
      return setIsCompiled(true);
    }
    onChange({ ...state, sourceCode: value });
    setIsCompiled(false);
  };

  const updateReadMe = (value: string) => {
    onChange({ ...state, readme: value });
  };

  const updateDetection = (value: string, id: string, revert = false) => {
    onChange({
      ...state,
      detections: state.detections.map((item) =>
        item.id === id
          ? {
              ...item,
              rule: value,
              status: revert
                ? "unchanged"
                : item.status === "created"
                  ? "created"
                  : "modified",
            }
          : item,
      ),
    });
  };

  const addDetection = () => {
    onChange({
      ...state,
      detections: [
        ...state.detections,
        {
          id: v4(),
          rule: ruleTemplate,
          status: "created",
        },
      ],
    });
  };

  const removeDetection = (id: string) => {
    const detection = state.detections.find((item) => item.id === id);
    if (detection?.status === "created") {
      onChange({
        ...state,
        detections: state.detections.filter((item) => item.id !== id),
      });
    } else {
      onChange({
        ...state,
        detections: state.detections.map((item) =>
          item.id === id ? { ...item, status: "deleted" } : item,
        ),
      });
    }
  };

  const updateThreatHunt = (
    property: "name" | "query" | "control",
    value: string | ControlCode,
    id: string,
    revert = false,
  ) => {
    onChange({
      ...state,
      threatHunts: state.threatHunts.map((item) =>
        item.id === id
          ? {
              ...item,
              [property]: value,
              status: revert
                ? "unchanged"
                : item.status === "created"
                  ? "created"
                  : "modified",
            }
          : item,
      ),
    });
  };

  const addThreatHunt = () => {
    onChange({
      ...state,
      threatHunts: [
        ...state.threatHunts,
        {
          id: v4(),
          name: "",
          query: "",
          control: 1,
          status: "created",
        },
      ],
    });
  };

  const removeThreatHunt = (id: string) => {
    const threatHunt = state.threatHunts.find((item) => item.id === id);
    if (threatHunt?.status === "created") {
      onChange({
        ...state,
        threatHunts: state.threatHunts.filter((item) => item.id !== id),
      });
      return;
    }
    onChange({
      ...state,
      threatHunts: state.threatHunts.map((item) =>
        item.id === id ? { ...item, status: "deleted" } : item,
      ),
    });
  };

  const compileJobId = compile.data?.job_id;

  const compiledQuery = useQuery({
    ...queries.build.getCompile(authConfig, compileJobId as string),
    enabled: Boolean(compileJobId),
    refetchInterval: (query) => {
      if (
        query.state.data?.status === "COMPLETE" ||
        query.state.data?.status === "FAILED"
      ) {
        return false;
      }
      return 3000;
    },
  });

  useEffect(() => {
    if (
      state.sourceCode === compiledSourceCode &&
      isCompiled === false &&
      compiledQuery.data?.results?.every(
        (result) =>
          result.status === "SKIPPED" || result.status === "SUCCEEDED",
      )
    ) {
      setIsCompiled(true);
    }
  }, [
    compiledQuery.data?.results,
    state,
    compiledSourceCode,
    isCompiled,
    setIsCompiled,
  ]);

  const isCompiling =
    compile.isPending ||
    compiledQuery.isFetching ||
    compiledQuery.data?.status === "RUNNING";

  const sourceCodeDisplayName = state.sourceCode
    ? state.sourceCode.match(/^NAME:\s*(\S+)/m)?.[1]
    : "sourcecode";

  const visibleDetections = state.detections.filter(
    (d) => d.status !== "deleted",
  );
  const visibleThreatHunts = state.threatHunts.filter(
    (t) => t.status !== "deleted",
  );

  return (
    <div className="flex flex-col gap-4 overflow-y-auto">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <ToggleGroup value={activeTab} onValueChange={setActiveTab}>
          <ToggleItem value="readme">
            <div className="flex items-center gap-2">
              <InfoIcon className="size-4" />
              Description
            </div>
          </ToggleItem>

          <ToggleItem value="sourceCode">
            <div className="flex items-center gap-2">
              <CodeIcon className="size-4" />
              Test
            </div>
          </ToggleItem>

          <Flag features={["detections"]}>
            <ToggleItem value="detections">
              <div className="flex items-center gap-2">
                <SignalIcon className="size-4" />
                Custom Detection
              </div>
            </ToggleItem>
          </Flag>

          <Flag features={["threat_intel"]}>
            <ToggleItem value="threatHunts">
              <div className="flex items-center gap-2">
                <TargetIcon className="size-4" />
                Threat Hunting
              </div>
            </ToggleItem>
          </Flag>
        </ToggleGroup>
        {activeTab === "detections" && (
          <DefaultTooltip asChild content="Max of 3 supported">
            <Button
              type="button"
              intent="primary"
              icon={<PlusIcon />}
              iconPosition="left"
              onClick={addDetection}
              disabled={visibleDetections.length > 2}
            >
              Add
            </Button>
          </DefaultTooltip>
        )}
        {activeTab === "threatHunts" && (
          <DefaultTooltip asChild content="Max of 10 supported">
            <Button
              type="button"
              intent="primary"
              icon={<PlusIcon />}
              iconPosition="left"
              onClick={addThreatHunt}
              disabled={visibleThreatHunts.length > 9}
            >
              Add
            </Button>
          </DefaultTooltip>
        )}
      </div>

      {activeTab === "readme" && (
        <>
          <div className="flex-1 overflow-hidden">
            <Editor
              filename={"README.md"}
              onChange={updateReadMe}
              value={state.readme}
              editable={true}
              type="md"
              height="400px"
            />
          </div>
          <div className="flex items-center justify-end">
            <Button
              type="button"
              disabled={currentState.readme === state.readme}
              icon={<RestartIcon />}
              intent="secondary"
              onClick={() => {
                updateReadMe(currentState.readme);
              }}
            >
              Revert changes
            </Button>
          </div>
        </>
      )}

      {activeTab === "sourceCode" && (
        <>
          <div className="flex-1 overflow-hidden">
            <Editor
              filename={
                sourceCodeDisplayName &&
                formatFileName(sourceCodeDisplayName) + ".go"
              }
              onChange={updateSourceCode}
              value={state.sourceCode}
              editable={true}
              type="go"
              height="400px"
            />
          </div>
          <div className="flex items-start justify-between">
            <div className="w-3/4">
              {compiledQuery.data && (
                <CompileResults data={compiledQuery.data} />
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                disabled={
                  isCompiling || currentState.sourceCode === state.sourceCode
                }
                icon={<RestartIcon />}
                intent="secondary"
                onClick={() => {
                  compile.reset();
                  updateSourceCode(currentState.sourceCode);
                }}
              >
                Revert changes
              </Button>
              <Button
                type="button"
                loading={isCompiling}
                disabled={isCompiling || !state.sourceCode}
                intent="primary"
                onClick={() => {
                  setIsCompiled(false);
                  compile.mutate(
                    {
                      code: state.sourceCode,
                      source_test_id: state.sourceTestId,
                    },
                    {
                      onSuccess: () => setCompiledSourceCode(state.sourceCode),
                    },
                  );
                }}
              >
                Compile
              </Button>
            </div>
          </div>
        </>
      )}

      {activeTab === "detections" && (
        <EditDetections
          detections={visibleDetections}
          originalDetections={currentState.detections ?? []}
          updateFn={updateDetection}
          removeFn={removeDetection}
        />
      )}

      {activeTab === "threatHunts" && (
        <EditThreatHunts
          threatHunts={visibleThreatHunts}
          originalThreatHunts={currentState.threatHunts ?? []}
          updateFn={updateThreatHunt}
          removeFn={removeThreatHunt}
        />
      )}
    </div>
  );
};
